import { useState } from "react";
import Popover from "@mui/material/Popover";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import { useUserDisplayName, useUserEmail } from "hooks/settings";
import { useSignOut } from "hooks/useSignOut";
import { useTenantTranslation } from "hooks/formatters";
import { HotkeysToggle } from "./HotkeysToggle";
import { LowMemoryVideoModeToggle } from "./LowMemoryVideoModeToggle";
import { SettingsMenuButton } from "../SettingsMenuButton";

export const UserMenu = () => {
  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const displayName = useUserDisplayName();
  const email = useUserEmail();
  const signOut = useSignOut();
  const { t } = useTenantTranslation();

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleSignOut = () => {
    handleUserMenuClose();
    void signOut();
  };

  return (
    <>
      <SettingsMenuButton onClick={handleUserMenuClick}>
        <PersonRoundedIcon
          sx={{
            color: "white",
            fontSize: "1.25rem",
          }}
        />
      </SettingsMenuButton>

      <Popover
        open={Boolean(userMenuAnchor)}
        anchorEl={userMenuAnchor}
        onClose={handleUserMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{ mt: 1 }}
        slotProps={{
          paper: {
            sx: {
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              boxShadow: "none",
              overflow: "hidden",
            },
          },
        }}
      >
        <Paper
          sx={{
            p: 3,
            minWidth: 220,
            maxWidth: 280,
          }}
          elevation={0}
        >
          <Box sx={{ mb: 2.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 1,
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PersonRoundedIcon
                  sx={{
                    color: "white",
                    fontSize: "1.5rem",
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                  }}
                >
                  {email}
                </Typography>
              </Box>
            </Box>
          </Box>
          <HotkeysToggle />
          <LowMemoryVideoModeToggle />
          <Divider sx={{ mb: 2.5 }} />
          <Button
            variant="outlined"
            fullWidth
            startIcon={<LogoutRoundedIcon />}
            onClick={handleSignOut}
            size="medium"
            sx={{
              borderRadius: 1,
              borderColor: "divider",
              color: "text.secondary",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "action.hover",
              },
            }}
          >
            {t("signOut")}
          </Button>
        </Paper>
      </Popover>
    </>
  );
};
